import type { ApiContext } from "@/api/types";
import type { DraftNote, PatientConsent, PatientStatus, ProviderProfile, UUID } from "@/core/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";

type FetchProviderDraftNotesBody = {
  provider_id?: UUID;
};

type UpdatePatientStatusBody = {
  providerName?: string;
  reason?: string;
  status: PatientStatus;
  username: UUID;
};

export type UpdatePatientStatusResponse = {
  previous_status: PatientStatus;
};

export type UpdateProviderProfilePayload = {
  accept_new_patients?: boolean;
  accepted_age_range?: string[];
  accepted_insurance?: string[];
  cp_credentials?: string;
  mobile?: string;
  name?: string;
  npi_number?: string;
  preferences?: Record<string, unknown>;
  states_to_practice?: Array<{
    expirationDate: string;
    licenseNumber: string;
    state: string;
  }>;
};

export const providerRequestsTyped = {
  delete_progress_note: function ({
    data,
    urlParams,
  }: {
    data: { id: UUID };
    urlParams: { patientId: UUID };
  }): Promise<void> {
    return httpAuth2
      .post(`/v1/patient_management/${urlParams.patientId}/delete_progress_notes`, {
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json<void>();
  },

  fetch_provider_draft_notes: function ({
    context,
    data,
  }: { context?: ApiContext; data?: FetchProviderDraftNotesBody } = {}) {
    return httpAuth2
      .post("/v1/provider/get_provider_draft_notes", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(data !== undefined && { json: data }),
      })
      .json<DraftNote[]>();
  },

  fetch_provider_profile: function ({
    context,
    providerId,
  }: {
    context?: ApiContext;
    providerId?: UUID;
  } = {}) {
    return httpAuth2
      .post("/v1/provider/profile_get", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(providerId !== undefined && { searchParams: { provider_id: providerId } }),
      })
      .json<{ user: ProviderProfile }>();
  },

  get_patient_consents: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: UUID;
  }) {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/get_latest_consents`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<PatientConsent[]>();
  },

  update_patient_status: function ({
    context,
    data,
    patientId,
  }: {
    context?: ApiContext;
    data: UpdatePatientStatusBody;
    patientId: UUID;
  }) {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/update_patient_details`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json<UpdatePatientStatusResponse>();
  },

  update_profile: function ({
    context,
    data,
    providerId,
  }: {
    context?: ApiContext;
    data: UpdateProviderProfilePayload;
    providerId?: UUID;
  }) {
    return httpAuth2
      .post("/v2/provider/profile_update", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        ...(providerId !== undefined && { searchParams: { provider_id: providerId } }),
      })
      .json<void>();
  },
};
